import { useEffect, useMemo, useRef, useState } from 'react';
import { adminApi } from '../../api/admin';
import { useAsync, Banner } from './ui';
import { pmsGroupLabel } from '../../lib/pmsGroup';
import { statusUi } from '../../lib/statusUi';
import type { ReadingStatus } from '../../lib/schedule';
import { capturedNowIso } from '../../lib/serverTime';
import { shareSchedulePdf } from '../../lib/shareSchedule';
import type { ScheduleDayGroup } from '../../lib/schedulePdf';

type Row = Record<string, unknown>;
type DayMap = Record<string, number | null>;

function dayOf(r: Row): number | null {
  const v = r['PMS Cycle Day'];
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** date-only ("2026-09-13") arithmetic done in UTC so DST never shifts the calendar day. */
function addDaysISO(dateOnly: string, days: number): string {
  const [y, m, d] = dateOnly.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
function fmtDayLabel(dateOnly: string): string {
  const [y, m, d] = dateOnly.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/**
 * A day-by-day board for the rotating PMS schedule. Drag a card to a new day
 * (or tap to pick it up, then tap a column) — every move only touches local
 * state, instantly. Nothing reaches the server until "Save changes", which
 * batches the whole board into one request.
 */
export default function PmsSchedulerAdmin() {
  const equipment = useAsync(() => adminApi.listEquipment(), [], 'equipment');
  const templates = useAsync(() => adminApi.listTemplates(), [], 'templates');
  const dashboard = useAsync(() => adminApi.dashboard(), [], 'adminDashboard');
  const config = useAsync(() => adminApi.getConfig(), [], 'config');
  const [group, setGroup] = useState('');

  const [dayById, setDayById] = useState<DayMap>({});
  const [baseline, setBaseline] = useState<DayMap>({});
  const seeded = useRef(false);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadMsg, setDownloadMsg] = useState('');

  const pmsEquip = useMemo(
    () => (equipment.data?.rows ?? []).filter((r) => String(r['Category'] ?? 'daily') === 'pms'),
    [equipment.data],
  );

  const dirty = useMemo(
    () => Object.keys(dayById).filter((id) => dayById[id] !== baseline[id]),
    [dayById, baseline],
  );

  // Seed local state once data first arrives; never clobber an in-progress edit
  // with a background revalidation of the same cache key.
  useEffect(() => {
    if (!equipment.data) return;
    if (seeded.current && dirty.length) return;
    const map: DayMap = {};
    pmsEquip.forEach((r) => { map[String(r['Equipment ID'])] = dayOf(r); });
    setDayById(map);
    setBaseline(map);
    seeded.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment.data]);

  const tplTitleById = useMemo(() => {
    const m = new Map<string, string>();
    templates.data?.templates.forEach((t) => m.set(t.templateId, t.title));
    return m;
  }, [templates.data]);
  const groupOf = (r: Row) => pmsGroupLabel(tplTitleById.get(String(r['Template ID'])) ?? String(r['Template ID']));

  const statusByEquip = useMemo(() => {
    const m = new Map<string, { status: string; lastReadingAt: string | null }>();
    dashboard.data?.equipment.forEach((e) => m.set(e.equipmentId, { status: e.status, lastReadingAt: e.lastReadingAt }));
    return m;
  }, [dashboard.data]);

  const cycleLen = Math.max(1, Number(config.data?.effective.pmsCycleLengthDays) || 15);
  const anchorDate = String(config.data?.effective.pmsCycleAnchorDate ?? '');
  const todayDay = dashboard.data?.todayPmsCycleDay ?? null;

  const groups = useMemo(() => {
    const s = new Set<string>();
    pmsEquip.forEach((r) => s.add(groupOf(r)));
    return [...s].sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmsEquip, tplTitleById]);

  const shown = group ? pmsEquip.filter((r) => groupOf(r) === group) : pmsEquip;

  const byDay = useMemo(() => {
    const m = new Map<number | null, Row[]>();
    for (let d = 1; d <= cycleLen; d++) m.set(d, []);
    m.set(null, []);
    shown.forEach((r) => {
      const id = String(r['Equipment ID']);
      const d = id in dayById ? dayById[id] : dayOf(r);
      const key = d !== null && d >= 1 && d <= cycleLen ? d : null;
      m.get(key)!.push(r);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, cycleLen, dayById]);

  const counts = Array.from({ length: cycleLen }, (_, i) => byDay.get(i + 1)!.length);
  const busiest = Math.max(0, ...counts);
  const avg = counts.length ? counts.reduce((a, b) => a + b, 0) / counts.length : 0;

  function moveLocal(id: string, day: number | null) {
    setDayById((prev) => (prev[id] === day ? prev : { ...prev, [id]: day }));
  }

  // Tap a card to pick it up; tap it again to cancel; tap ANY other card (or empty
  // column space) while one is picked drops it into that card's/column's day.
  function pickOrMove(id: string, colDay: number | null) {
    if (pickedId === id) { setPickedId(null); return; }
    if (pickedId) { moveLocal(pickedId, colDay); setPickedId(null); return; }
    setPickedId(id);
  }

  // `draggedId` comes straight off the drop event's dataTransfer — never off React
  // state — so a fast drag (dragstart+drop before a re-render commits) can't read a
  // stale closure and silently no-op.
  function dropOnColumn(day: number | null, draggedId: string | null) {
    if (pickedId) { moveLocal(pickedId, day); setPickedId(null); return; }
    if (draggedId) moveLocal(draggedId, day);
  }

  async function save() {
    if (!dirty.length || saving) return;
    setSaving(true); setSaveMsg('');
    try {
      await adminApi.setPmsDays(dirty.map((id) => ({ equipmentId: id, pmsCycleDay: dayById[id] })));
      setBaseline((b) => ({ ...b, ...Object.fromEntries(dirty.map((id) => [id, dayById[id]])) }));
      equipment.reload();
      dashboard.reload();
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setDayById(baseline);
    setPickedId(null);
  }

  async function downloadPdf() {
    if (downloading) return;
    setDownloading(true); setDownloadMsg('');
    try {
      const todayDateOnly = capturedNowIso().slice(0, 10);
      const anchorDay = todayDay ?? 1;
      const dayGroups: ScheduleDayGroup[] = [];

      const unschedRows = byDay.get(null)!;
      if (unschedRows.length) {
        dayGroups.push({
          day: null, label: 'Unscheduled', dateLabel: '',
          rows: unschedRows.map((r) => ({ name: String(r['Equipment Name'] ?? r['Equipment ID']), group: groupOf(r), location: String(r['Location'] ?? '') })),
        });
      }
      for (let d = 1; d <= cycleLen; d++) {
        const offset = ((d - anchorDay) % cycleLen + cycleLen) % cycleLen;
        const dateOnly = addDaysISO(todayDateOnly, offset);
        const rows = byDay.get(d)!;
        dayGroups.push({
          day: d, label: `Day ${d}`, dateLabel: fmtDayLabel(dateOnly), today: d === todayDay,
          rows: rows.map((r) => ({ name: String(r['Equipment Name'] ?? r['Equipment ID']), group: groupOf(r), location: String(r['Location'] ?? '') })),
        });
      }

      const windowStartLabel = fmtDayLabel(todayDateOnly);
      const windowEndLabel = fmtDayLabel(addDaysISO(todayDateOnly, cycleLen - 1));
      await shareSchedulePdf(
        {
          cycleLenDays: cycleLen,
          windowStartLabel, windowEndLabel,
          generatedAtLabel: new Date(capturedNowIso()).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
          totalItems: shown.length,
          groups: dayGroups,
        },
        `${todayDateOnly}_to_${addDaysISO(todayDateOnly, cycleLen - 1)}`,
      );
    } catch (e) {
      setDownloadMsg(e instanceof Error ? e.message : 'Could not build the PDF.');
    } finally {
      setDownloading(false);
    }
  }

  const loading = equipment.loading || templates.loading || config.loading;

  return (
    <div className="sched">
      <div className="spread">
        <h1>PMS scheduler</h1>
        <button type="button" className="secondary sm" onClick={downloadPdf} disabled={downloading || loading}>
          {downloading ? 'Building…' : '⬇ Download PDF'}
        </button>
      </div>
      {downloadMsg && <p className="error tiny">{downloadMsg}</p>}
      <p className="muted tiny" style={{ marginTop: 2 }}>
        {pmsEquip.length} PMS items on a {cycleLen}-day rotation
        {anchorDate ? <> · anchored {anchorDate}</> : null}
        {todayDay ? <> · today is <strong>day {todayDay}</strong></> : null}.
        Drag a card to a new day, or tap it and then tap a column.
      </p>

      {!loading && (
        <div className="choice-group" style={{ margin: '10px 0 14px' }}>
          <button type="button" className={`choice-btn ${group === '' ? 'on' : ''}`} onClick={() => setGroup('')}>
            All ({pmsEquip.length})
          </button>
          {groups.map((g) => (
            <button key={g} type="button" className={`choice-btn ${group === g ? 'on' : ''}`} onClick={() => setGroup(g)}>
              {g} ({pmsEquip.filter((r) => groupOf(r) === g).length})
            </button>
          ))}
        </div>
      )}

      {loading && <div className="spinner" />}

      {!loading && (
        <div className={`schedGrid ${pickedId ? 'picking' : ''}`}>
          <SchedCol
            dayKey={null} label="Unscheduled" count={byDay.get(null)!.length} today={false} warn
            rows={byDay.get(null)!} groupOf={groupOf} statusByEquip={statusByEquip}
            draggingId={draggingId} pickedId={pickedId}
            dragOverKey={dragOverKey} setDragOverKey={setDragOverKey}
            onDragStart={setDraggingId} onDragEnd={() => setDraggingId(null)}
            onDrop={dropOnColumn} onPick={pickOrMove}
            empty="None — every PMS item has a day."
          />
          {Array.from({ length: cycleLen }, (_, i) => i + 1).map((d) => (
            <SchedCol
              key={d} dayKey={d} label={`Day ${d}`} count={byDay.get(d)!.length} today={d === todayDay}
              heavy={busiest > 0 && avg > 0 && byDay.get(d)!.length >= Math.max(avg * 1.6, avg + 3)}
              rows={byDay.get(d)!} groupOf={groupOf} statusByEquip={statusByEquip}
              draggingId={draggingId} pickedId={pickedId}
              dragOverKey={dragOverKey} setDragOverKey={setDragOverKey}
              onDragStart={setDraggingId} onDragEnd={() => setDraggingId(null)}
              onDrop={dropOnColumn} onPick={pickOrMove}
              empty="Nothing scheduled."
            />
          ))}
        </div>
      )}

      <div className={`schedBar ${dirty.length ? 'show' : ''}`}>
        {saveMsg
          ? <Banner error={saveMsg} />
          : <span>{dirty.length} change{dirty.length === 1 ? '' : 's'} pending</span>}
        <div className="schedBar-acts">
          <button type="button" className="secondary" onClick={discard} disabled={saving}>Discard</button>
          <button type="button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

function SchedCol(props: {
  dayKey: number | null; label: string; count: number; today: boolean; warn?: boolean; heavy?: boolean; empty: string;
  rows: Row[]; groupOf: (r: Row) => string;
  statusByEquip: Map<string, { status: string; lastReadingAt: string | null }>;
  draggingId: string | null; pickedId: string | null;
  dragOverKey: string | null; setDragOverKey: (k: string | null) => void;
  onDragStart: (id: string) => void; onDragEnd: () => void;
  onDrop: (day: number | null, draggedId: string | null) => void; onPick: (id: string, colDay: number | null) => void;
}) {
  const {
    dayKey, label, count, today, warn, heavy, empty, rows, groupOf, statusByEquip,
    draggingId, pickedId, dragOverKey, setDragOverKey, onDragStart, onDragEnd, onDrop, onPick,
  } = props;
  const key = String(dayKey ?? 'unsched');
  const isOver = dragOverKey === key;
  return (
    <div
      className={`schedCol ${today ? 'today' : ''} ${warn ? 'warnCol' : ''} ${isOver ? 'over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== key) setDragOverKey(key); }}
      onDragLeave={() => { if (dragOverKey === key) setDragOverKey(null); }}
      onDrop={(e) => { e.preventDefault(); setDragOverKey(null); onDrop(dayKey, e.dataTransfer.getData('text/plain') || draggingId); }}
      onClick={() => { if (pickedId) onDrop(dayKey, null); }}
    >
      <div className="schedColHead">
        <span>{label}{today ? ' · today' : ''}</span>
        <span className={`schedColCount ${heavy ? 'heavy' : ''}`}>{count}</span>
      </div>
      <div className="schedColBody">
        {rows.length === 0 && <p className="muted tiny" style={{ padding: '6px 2px' }}>{empty}</p>}
        {rows.map((r) => {
          const id = String(r['Equipment ID']);
          return (
            <SchedCard
              key={id} r={r} group={groupOf(r)}
              info={statusByEquip.get(id)}
              dragging={draggingId === id} picked={pickedId === id}
              onDragStart={() => onDragStart(id)} onDragEnd={onDragEnd}
              onPick={(e) => { e.stopPropagation(); onPick(id, dayKey); }}
            />
          );
        })}
      </div>
    </div>
  );
}

function SchedCard({ r, group, info, dragging, picked, onDragStart, onDragEnd, onPick }: {
  r: Row; group: string;
  info?: { status: string; lastReadingAt: string | null };
  dragging: boolean; picked: boolean;
  onDragStart: () => void; onDragEnd: () => void;
  onPick: (e: React.MouseEvent) => void;
}) {
  const id = String(r['Equipment ID']);
  const st = info ? statusUi(info.status as ReadingStatus) : null;
  return (
    <div
      className={`schedCard ${dragging ? 'dragging' : ''} ${picked ? 'picked' : ''}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move'; onDragStart(); }}
      onDragEnd={onDragEnd}
      onClick={onPick}
      title={String(r['Location'] ?? '')}
    >
      {st && <span className={`schedDot ${st.tone}`} />}
      <div className="schedCard-text">
        <div className="schedCard-name">{String(r['Equipment Name'] ?? id)}</div>
        <div className="schedCard-group">{group}</div>
      </div>
    </div>
  );
}
